import { createListenerMiddleware, isAnyOf } from '@reduxjs/toolkit'
import { AppDispatch, RootState } from '@/store/store'
import { UserFilesBatchingActions, UserFilesBatchedOperationKind, UserFilesBatchedOperationStatus } from './slice'
import { selectActiveUserFilesOperation } from './selectors'
import { executeUserFilesBatchedOperation, getUserFilesOperationErrorMessage } from '@/user_files/batching/utils/execute-user-files-batched-operation'
import { UserFoldersActions } from '@/user_files/folders/store/slice'
import { UserFilesActions } from '@/user_files/files/store/slice'
import { fetchAndStoreUserFiles } from '@/user_files/files/utils/fetch-and-store-user-files'
import { selectCurrentFolderId } from '@/user_files/folders/store/selectors'
import { getFolderAncestorIds } from '@/user_files/folders/utils/get-folder-ancestor-ids'
import { UppyFileUploadActions } from '@/uppy-file-upload/store/slice'

export const userFilesBatchingListenerMiddleware = createListenerMiddleware()
const startListener = userFilesBatchingListenerMiddleware.startListening.withTypes<RootState, AppDispatch>()

startListener({
  matcher: isAnyOf(UserFilesBatchingActions.enqueueMoveSelection, UserFilesBatchingActions.enqueueDeleteSelection),
  effect: async (_action, listenerApi) => {
    const op = selectActiveUserFilesOperation(listenerApi.getState())
    if (!op || op.status !== UserFilesBatchedOperationStatus.Queued) return

    listenerApi.dispatch(UserFilesBatchingActions.operationStarted({ operationId: op.id }))

    const active = selectActiveUserFilesOperation(listenerApi.getState())
    if (!active || active.id !== op.id) return

    const foldersToRefresh = Array.from(new Set([active.refreshFolderId, active.destinationFolderId].filter((id): id is string => Boolean(id))))

    try {
      const foldersRecord = listenerApi.getState().userFoldersState.folders

      const result = await executeUserFilesBatchedOperation({
        kind: active.kind,
        destinationFolderId: active.destinationFolderId,
        fileIds: active.fileIds,
        folderIds: active.folderIds,
        filePreconditions: active.filePreconditions,
        folderPreconditions: active.folderPreconditions,
        destinationFolderPrecondition: active.destinationFolderPrecondition,
        deleteMode: active.deleteMode,
        isRootFolderId: (folderId) => Boolean(foldersRecord[folderId]?.data.is_root),
        onProgress: (count) => {
          listenerApi.dispatch(UserFilesBatchingActions.operationProgressUpdated({ operationId: active.id, processedItems: count }))
        },
      })

      // Keep upload rows aligned with successful file operations so conflict detection does not reserve stale names.
      if (result.kind === UserFilesBatchedOperationKind.DeleteSelection) {
        listenerApi.dispatch(
          UppyFileUploadActions.filesRemovedByServerFileIds({
            serverFileIds: [...result.aggregate.deleted_file_ids, ...result.aggregate.unchanged_file_ids],
          })
        )
        // Anything still holding a deleted file learns it's gone now, rather than when it's next fetched
        listenerApi.dispatch(UserFilesActions.markUserFilesUnavailable(result.aggregate.deleted_file_ids))
      }
      if (result.kind === UserFilesBatchedOperationKind.MoveSelection) {
        listenerApi.dispatch(
          UppyFileUploadActions.filesMovedByServerFileIds({
            serverFileIds: [...result.aggregate.moved_file_ids, ...result.aggregate.unchanged_file_ids],
            folderId: result.aggregate.destination_folder_id,
          })
        )
        // Cached records still have the file's old folder and matter, so they're refetched
        const filesById = listenerApi.getState().userFilesState.filesById
        const cachedMovedFileIds = result.aggregate.moved_file_ids.filter((fileId) => filesById[fileId])
        fetchAndStoreUserFiles(cachedMovedFileIds, listenerApi.dispatch).catch(() => undefined)
      }

      listenerApi.dispatch(UserFilesBatchingActions.operationSucceeded({ operationId: active.id }))
    } catch (cause) {
      const errorMessage = getUserFilesOperationErrorMessage({ kind: active.kind, cause })
      listenerApi.dispatch(UserFilesBatchingActions.operationFailed({ operationId: active.id, errorMessage }))
    } finally {
      // If the user navigated away before completion, mark the launching folder's
      // child-folder cache stale so revisiting it triggers a refetch.
      listenerApi.dispatch(UserFoldersActions.invalidateFolderChildren({ folderId: active.refreshFolderId }))

      // A folder's `file_count` covers its whole subtree, so moving or deleting files changes the
      // count on every ancestor of both ends of the operation. Those counts live in the ancestors'
      // children listings, which the refresh below does not touch — mark them stale so the next
      // visit refetches. A move has two trees to fix; a delete only has the source. The slice drops
      // each end itself: its own row lives in its parent's listing, not in its own.
      const foldersById = listenerApi.getState().userFoldersState.folders
      const staleCountFolderIds = new Set(
        [active.refreshFolderId, active.destinationFolderId]
          .filter((folderId): folderId is string => Boolean(folderId))
          .flatMap((folderId) => getFolderAncestorIds(foldersById, folderId).slice(0, -1))
      )
      staleCountFolderIds.forEach((folderId) => listenerApi.dispatch(UserFoldersActions.invalidateFolderChildren({ folderId })))

      // Server is the source of truth: refresh on success AND failure.
      // Only refresh folders that are still relevant (current folder and destination folder)
      // to avoid unnecessary network requests after user navigation.
      const currentFolderId = selectCurrentFolderId(listenerApi.getState())
      const relevantFolders = foldersToRefresh.filter((folderId) => {
        // Always refresh the current folder
        if (folderId === currentFolderId) return true
        // Also refresh if user navigated into the destination folder
        if (folderId === active.destinationFolderId) return true
        return false
      })

      relevantFolders.forEach((folderId) => {
        listenerApi.dispatch(UserFoldersActions.fetchFolderChildren({ folderId }))
        listenerApi.dispatch(UserFilesActions.fetchFilesForFolder({ folderId }))
      })
    }
  },
})
